import { Component, HostListener, OnInit, Renderer2 } from '@angular/core'
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser'
import { StripHtmlPipe } from 'src/app/shared/pipes/strip-html.pipe'

import { Router } from '@angular/router'
import { FeaturedCardLayout } from 'src/app/shared/enums/featured-card-layout.enum'
import { ExpertGroupPortal } from 'src/app/shared/interfaces/expert-group-portal.interface'
import { FeaturedCard } from 'src/app/shared/interfaces/resources/featured-card.interface'
import { FeaturedCardService } from 'src/app/shared/services/featured-card.service'
import { Author } from '../../../../shared/interfaces/author.interface'
import { BreadcrumbLink } from '../../../../shared/interfaces/breadcrumb-link.interface'
import { Discussion } from '../../../../shared/interfaces/discussion.interface'
import { Page } from '../../../../shared/interfaces/page.interface'
import { Post } from '../../../../shared/interfaces/resources/post.interface'
import { TaxonomyTerm } from '../../../../shared/interfaces/taxonomy-term.interface'
import { AuthorService } from '../../../../shared/services/author.service'
import { MetaService } from '../../../../shared/services/meta.service'
import { PageService } from '../../../../shared/services/page-service/page.service'
import { PostService } from '../../../../shared/services/post.service'
import { TagService } from '../../../../shared/services/tag.service'
import { DiscussionService } from '../../../expert-group-portal/services/discussion.service'
import { ExpertGroupPortalService } from '../../services/expert-group-portal.service'

@Component({
  selector: 'app-expert-group-portal-overview',
  templateUrl: './expert-group-portal-overview.component.html',
  styleUrls: ['./expert-group-portal-overview.component.scss'],
})
export class ExpertGroupPortalOverviewComponent implements OnInit {
  breadcrumbLinks: BreadcrumbLink[]

  discussions: Discussion[]
  authors: Author[]
  tags: TaxonomyTerm[] = []
  page: Page

  featuredCards: FeaturedCard[]
  FeaturedCardLayout = FeaturedCardLayout

  loadingPage = true
  loadingDiscussions = true
  loadingAuthors = true
  loadingRelatedPosts = true
  loadingFeaturedCards = true

  baseUrl: string
  futuresAiNewsUrl: SafeResourceUrl
  videoUrl: SafeResourceUrl

  expertGroupPortal: ExpertGroupPortal

  isModalOpen = false

  constructor(
    private pageService: PageService,
    private postService: PostService,
    private authorService: AuthorService,
    private tagService: TagService,
    private discussionService: DiscussionService,
    private featuredCardService: FeaturedCardService,
    private expertGroupPortalService: ExpertGroupPortalService,
    private metaService: MetaService,
    private stripHtmlPipe: StripHtmlPipe,
    private sanitizer: DomSanitizer,
    private router: Router,
    private renderer: Renderer2
  ) {}

  async ngOnInit() {
    // Get the current expert group portal without the query params
    this.baseUrl = this.router.url.split('?')[0]

    // The id of the current expert group portal, it's always one string before the last slash
    const id = this.baseUrl.split('/').pop()
    this.expertGroupPortal =
      this.expertGroupPortalService.getExpertGroupPortalById(id)

    this.page = await this.pageService.show(
      this.expertGroupPortal.slug + '-overview'
    )
    this.loadingPage = false

    this.videoUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
      this.page.link1
    )

    this.futuresAiNewsUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
      'https://oecd-aiobservatory.ijs.si/dashboards/AI/Embed?visualization=AI%20futures---news--ai-futures-live-news'
    )

    this.postService
      .listUnpaginated({
        tagIds: this.page.relatedPostTagIds,
        limitTo: 3
      })
      .then((postRes: Post[]) => {
        this.page.relatedPosts = postRes
        this.loadingRelatedPosts = false
      })
      .catch((err) => {
        this.loadingRelatedPosts = false
        console.error(err)
      })

    this.tags = await this.tagService.listByIds(this.page.relatedPostTagIds)

    this.discussionService
      .listUnpaginated({ tagIds: this.page.relatedPostTagIds })
      .then((discussionRes: Discussion[]) => {
        this.discussions = discussionRes
          .filter((discussion) => discussion.order)
          .sort((a, b) => b.order - a.order)
          .slice(0, 3)

        this.loadingDiscussions = false
      })
      .catch((err) => {
        this.loadingRelatedPosts = false
        console.error(err)
      })
    this.authorService
      .list({
        featuredFutures: this.expertGroupPortal.id === 'ai-futures',
        featuredComputeClimate: this.expertGroupPortal.id === 'compute-climate',
        featuredIncidents: this.expertGroupPortal.id === 'incidents',
        featuredRiskAccountability: this.expertGroupPortal.id === 'risk-accountability',
        featuredHealth: this.expertGroupPortal.id === 'health',
        featuredDataPrivacy: this.expertGroupPortal.id === 'data-privacy',
        withoutPagination: true
      })
      .then((authorRes) => {
        this.authors = authorRes as Author[]
        this.loadingAuthors = false
      })
      .catch((err) => {
        this.loadingRelatedPosts = false
        console.error(err)
      })

    this.featuredCards = this.page.featuredCardIds
      ? await this.featuredCardService.listByIds(this.page.featuredCardIds)
      : []
    this.loadingFeaturedCards = false

    this.metaService.setTags({
      title: this.page.metaTitle || this.page.title,
      description: this.stripHtmlPipe.transform(
        this.page.metaDescription || this.page.excerpt
      ),
      path: this.expertGroupPortal.id,
      absoluteOgImage: this.page.image
        ? this.page.image.socialCard ||
          this.page.image.landscape ||
          this.page.image.videoCard
        : this.metaService.defaultMeta.absoluteOgImage
    })
  }

  @HostListener('document:click', ['$event.target'])
  onClick(target: HTMLElement) {
    // We track clicks to close model if open and click outside
    if (this.isModalOpen && target.className === 'modal-background') {
      this.isModalOpen = false
      // Stop YouTube video from playing by resetting its source.
      const playerEl: Element = document.querySelector('#youtube-player')
      this.renderer.setAttribute(playerEl, 'src', '')
    }
  }
}
